import {
  timelineRouteSchema,
  type GraphEdge,
  type GraphNode,
  type LocalizedText,
  type MythDataset,
  type TimelineRoute,
  type TimelineScene,
  type TimelineSubEra,
  type TimelineTarget,
  type TimelineTick,
} from './data/schema'
import { timelineSceneNarrativeOverrides } from './timelineNarratives'
import {
  greekCoreEightAgesProgramme,
  norseFenrirBindingProgramme,
  timelineProgrammeForDataset,
  type TimelineProgramme,
  type TimelineProgrammeSegment,
} from './timelineProgramme'

export function timelineDelayMs(speed: 0.5 | 1 | 2) {
  return speed === 0.5 ? 12000 : speed === 2 ? 3000 : 6000
}

const excludedReason: LocalizedText = {
  'zh-Hant': '這個人物、地點或事件未在目前八時代故事順序中登場；仍可透過搜尋或神話地圖查看詳情。',
  en: 'This figure, place, or event does not appear in the current eight-age story sequence; it can still be opened through search or the myth atlas.',
}

const norseExcludedReason: LocalizedText = {
  'zh-Hant': '這個人物、地點或事件未在目前北歐故事模式時間線中登場；仍可透過搜尋或神話地圖查看詳情。',
  en: 'This figure, place, or event does not appear in the current Norse Story Mode timeline; it can still be opened through search or the myth atlas.',
}

function targetKey(target: TimelineTarget) {
  return target.kind === 'node' ? `node:${target.nodeId}` : `edge:${target.edgeId}`
}

function segmentRecords(dataset: MythDataset, segment: TimelineProgrammeSegment) {
  const sourceSteps = segment.sourceSteps?.length
    ? segment.sourceSteps
    : [{ storyId: segment.storyId, stepId: segment.stepId }]
  const records = sourceSteps.map((sourceStep) => {
    const story = dataset.stories.find((item) => item.id === sourceStep.storyId)
    if (!story) throw new Error(`Timeline references missing story ${sourceStep.storyId}`)
    if (story.status !== 'available') throw new Error(`Timeline references unavailable story ${sourceStep.storyId}`)
    const step = story.steps.find((item) => item.id === sourceStep.stepId)
    if (!step) throw new Error(`Timeline references missing step ${sourceStep.storyId}/${sourceStep.stepId}`)
    return { story, step, storyId: sourceStep.storyId, stepId: sourceStep.stepId }
  })
  const primary = records.find((record) => record.storyId === segment.storyId && record.stepId === segment.stepId) ?? records[0]
  const availableNodeIds = new Set(records.flatMap((record) => record.step.nodeIds))
  const availableEdgeIds = new Set(records.flatMap((record) => record.step.edgeIds))
  const nodeIds = segment.nodeIds ?? [...availableNodeIds]
  const edgeIds = segment.edgeIds ?? [...availableEdgeIds]
  const invalidNodes = nodeIds.filter((id) => !availableNodeIds.has(id))
  const invalidEdges = edgeIds.filter((id) => !availableEdgeIds.has(id))
  if (invalidNodes.length) throw new Error(`Timeline segment ${segment.sceneId ?? segment.stepId} selects nodes outside its source steps: ${invalidNodes.join(', ')}`)
  if (invalidEdges.length) throw new Error(`Timeline segment ${segment.sceneId ?? segment.stepId} selects edges outside its source steps: ${invalidEdges.join(', ')}`)
  return { ...primary, sourceSteps, records, nodeIds, edgeIds }
}

type TimelineNamePair = {
  id: string
  zhName: string
  enName: string
}

function canonicalName(value: string) {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, '')
    .toLowerCase()
}

function timelineNamePairForNode(node: GraphNode): TimelineNamePair | undefined {
  const zhName = node.name['zh-Hant'].trim()
  const enName = node.name.en.trim()
  if (!zhName || !enName || canonicalName(zhName) === canonicalName(enName)) return undefined
  return { id: node.id, zhName, enName }
}

function addTargetNodeIds(
  target: TimelineTarget | undefined,
  edgeById: Map<string, GraphEdge>,
  nodeIds: Set<string>,
) {
  if (!target) return
  if (target.kind === 'node') {
    nodeIds.add(target.nodeId)
    return
  }
  const edge = edgeById.get(target.edgeId)
  if (!edge) return
  nodeIds.add(edge.source)
  nodeIds.add(edge.target)
}

function timelineNarrativeNamePairs(
  dataset: MythDataset,
  records: ReturnType<typeof segmentRecords>['records'],
  selectedNodeIds: string[],
  edgeIds: string[],
  edgeById: Map<string, GraphEdge>,
  primaryFocus: TimelineTarget,
  focusPath: TimelineTarget[],
) {
  const nodeById = new Map(dataset.nodes.map((node) => [node.id, node]))
  const relevantNodeIds = new Set<string>(selectedNodeIds)
  records.forEach((record) => record.step.nodeIds.forEach((nodeId) => relevantNodeIds.add(nodeId)))
  edgeIds.forEach((edgeId) => addTargetNodeIds({ kind: 'edge', edgeId }, edgeById, relevantNodeIds))
  addTargetNodeIds(primaryFocus, edgeById, relevantNodeIds)
  focusPath.forEach((target) => addTargetNodeIds(target, edgeById, relevantNodeIds))

  return [...relevantNodeIds]
    .map((nodeId) => {
      const node = nodeById.get(nodeId)
      return node ? timelineNamePairForNode(node) : undefined
    })
    .filter((pair): pair is TimelineNamePair => Boolean(pair))
    .sort((left, right) => right.zhName.length - left.zhName.length || left.id.localeCompare(right.id))
}

function rangesForNames(text: string, names: string[]) {
  return names.flatMap((name) => {
    const ranges: Array<{ start: number; end: number }> = []
    let index = text.indexOf(name)
    while (index !== -1) {
      ranges.push({ start: index, end: index + name.length })
      index = text.indexOf(name, index + 1)
    }
    return ranges
  })
}

function firstStandaloneMentionIndex(text: string, pair: TimelineNamePair, pairs: TimelineNamePair[]) {
  const longerRanges = rangesForNames(
    text,
    pairs
      .filter((candidate) => candidate.id !== pair.id && candidate.zhName.length > pair.zhName.length)
      .map((candidate) => candidate.zhName),
  )
  let index = text.indexOf(pair.zhName)
  while (index !== -1) {
    const end = index + pair.zhName.length
    const isInsideLongerName = longerRanges.some((range) => index >= range.start && end <= range.end)
    if (!isInsideLongerName) return index
    index = text.indexOf(pair.zhName, index + 1)
  }
  return -1
}

function beginsWithEnglishPair(text: string, enName: string) {
  const escaped = enName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`^\\s*[（(]\\s*${escaped}\\s*[）)]`, 'i').test(text)
}

function addFirstBilingualMention(text: string, pair: TimelineNamePair, pairs: TimelineNamePair[]) {
  const index = firstStandaloneMentionIndex(text, pair, pairs)
  if (index === -1) return { text, mentioned: false }
  const end = index + pair.zhName.length
  if (beginsWithEnglishPair(text.slice(end), pair.enName)) return { text, mentioned: true }
  return {
    text: `${text.slice(0, end)}（${pair.enName}）${text.slice(end)}`,
    mentioned: true,
  }
}

function enrichTimelineNarrativeNames(
  narrative: TimelineProgrammeSegment['narrative'] | undefined,
  pairs: TimelineNamePair[],
): TimelineProgrammeSegment['narrative'] | undefined {
  if (!narrative || pairs.length === 0) return narrative
  const mentionedNodeIds = new Set<string>()
  const enrichText = (text: LocalizedText | undefined): LocalizedText | undefined => {
    if (!text) return text
    let zhText = text['zh-Hant']
    pairs.forEach((pair) => {
      if (mentionedNodeIds.has(pair.id)) return
      const result = addFirstBilingualMention(zhText, pair, pairs)
      zhText = result.text
      if (result.mentioned) mentionedNodeIds.add(pair.id)
    })
    return zhText === text['zh-Hant'] ? text : { ...text, 'zh-Hant': zhText }
  }
  return {
    setup: enrichText(narrative.setup),
    development: enrichText(narrative.development),
    consequence: enrichText(narrative.consequence),
  }
}

function makeTickId(subEraId: string, sequence: number, target: TimelineTarget) {
  const id = target.kind === 'node' ? target.nodeId : target.edgeId
  return `${subEraId}-${String(sequence).padStart(3, '0')}-${target.kind}-${id}`
}

export function buildTimelineRoute(
  dataset: MythDataset,
  programme: TimelineProgramme,
  excludedTargetReason: LocalizedText = excludedReason,
): TimelineRoute {
  const nodeIds = new Set(dataset.nodes.map((node) => node.id))
  const edgeById = new Map(dataset.edges.map((edge) => [edge.id, edge]))
  const introducedNodes = new Set<string>()
  const introducedEdges = new Set<string>()
  const referencedSteps = new Set<string>()
  const targetOccurrences = new Map<string, number>()
  const sceneIds = new Set<string>()
  let globalSceneOrder = 0

  const eras = programme.eras.map((era) => ({
    id: era.id,
    order: era.order,
    title: era.title,
    summary: era.summary,
    subEras: era.subEras.map((subEra) => {
      const ticks: TimelineTick[] = []
      const scenes: TimelineScene[] = []
      const append = (
        target: TimelineTarget,
        mode: TimelineTick['mode'],
        segment: TimelineProgrammeSegment,
      ) => {
        const occurrenceKey = targetKey(target)
        targetOccurrences.set(occurrenceKey, (targetOccurrences.get(occurrenceKey) ?? 0) + 1)
        const order = ticks.length + 1
        ticks.push({
          id: makeTickId(subEra.id, order, target),
          order,
          target,
          mode,
          storyId: segment.storyId,
          stepId: segment.stepId,
          chronologyNote: segment.chronologyNote,
        })
      }

      subEra.segments.forEach((segment) => {
        const {
          step,
          sourceSteps,
          records,
          nodeIds: selectedNodeIds,
          edgeIds,
        } = segmentRecords(dataset, segment)
        sourceSteps.forEach((sourceStep) => referencedSteps.add(`${sourceStep.storyId}:${sourceStep.stepId}`))
        const startTick = ticks.length

        selectedNodeIds.forEach((nodeId) => {
          if (!nodeIds.has(nodeId)) throw new Error(`Timeline references missing node ${nodeId}`)
          if (introducedNodes.has(nodeId)) return
          append({ kind: 'node', nodeId }, 'introduce', segment)
          introducedNodes.add(nodeId)
        })

        edgeIds.forEach((edgeId) => {
          const edge = edgeById.get(edgeId)
          if (!edge) throw new Error(`Timeline references missing edge ${edgeId}`)
          ;[edge.source, edge.target].forEach((nodeId) => {
            if (introducedNodes.has(nodeId)) return
            append({ kind: 'node', nodeId }, 'introduce', segment)
            introducedNodes.add(nodeId)
          })
          const mode = introducedEdges.has(edgeId) ? 'revisit' : 'introduce'
          append({ kind: 'edge', edgeId }, mode, segment)
          introducedEdges.add(edgeId)
        })

        const operationIds = ticks.slice(startTick).map((tick) => tick.id)
        const primaryFocus = segment.primaryFocus
          ?? ticks[ticks.length - 1]?.target
          ?? (selectedNodeIds[0] ? { kind: 'node' as const, nodeId: selectedNodeIds[0] }
            : edgeIds[0] ? { kind: 'edge' as const, edgeId: edgeIds[0] }
              : undefined)
        if (!primaryFocus) throw new Error(`Timeline scene ${segment.storyId}/${segment.stepId} has no focusable target`)
        const sceneId = segment.sceneId ?? `${segment.storyId}-${segment.stepId}`
        if (sceneIds.has(sceneId)) throw new Error(`Timeline scene ID is not unique: ${sceneId}. Split placements require an explicit sceneId.`)
        sceneIds.add(sceneId)
        const focusPath = segment.focusPath ?? []
        const narrative = enrichTimelineNarrativeNames(
          timelineSceneNarrativeOverrides[sceneId] ?? segment.narrative,
          timelineNarrativeNamePairs(dataset, records, selectedNodeIds, edgeIds, edgeById, primaryFocus, focusPath),
        )
        globalSceneOrder += 1
        scenes.push({
          id: sceneId,
          order: scenes.length + 1,
          globalOrder: globalSceneOrder,
          storyId: segment.storyId,
          stepId: segment.stepId,
          sourceSteps,
          title: segment.title ?? step.title,
          narrative,
          startTick,
          endTick: ticks.length,
          operationIds,
          graphNodeIds: selectedNodeIds,
          graphEdgeIds: edgeIds,
          primaryFocus,
          focusPath,
          chronologyNote: segment.chronologyNote,
        })
      })

      return {
        id: subEra.id,
        order: subEra.order,
        title: subEra.title,
        summary: subEra.summary,
        chronologyMode: subEra.chronologyMode,
        coverageStatus: subEra.coverageStatus,
        coverageNote: subEra.coverageNote,
        ticks,
        scenes,
      }
    }),
  }))

  const decisions = [
    ...dataset.nodes.map((node) => ({
      target: { kind: 'node' as const, nodeId: node.id },
      decision: introducedNodes.has(node.id) ? 'introduce' as const : 'excluded-with-reason' as const,
      reason: introducedNodes.has(node.id) ? undefined : excludedTargetReason,
      review: 'go' as const,
    })),
    ...dataset.edges.map((edge) => ({
      target: { kind: 'edge' as const, edgeId: edge.id },
      decision: introducedEdges.has(edge.id)
        ? (targetOccurrences.get(`edge:${edge.id}`) ?? 0) > 1 ? 'revisit' as const : 'introduce' as const
        : 'excluded-with-reason' as const,
      reason: introducedEdges.has(edge.id) ? undefined : excludedTargetReason,
      review: 'go' as const,
    })),
  ]
  const availableStorySteps = dataset.stories
    .filter((story) => story.status === 'available')
    .reduce((total, story) => total + story.steps.length, 0)

  return timelineRouteSchema.parse({
    id: programme.id,
    title: programme.title,
    description: programme.description,
    chronologyDisclaimer: programme.chronologyDisclaimer,
    eras,
    coverage: {
      decisions,
      availableStorySteps,
      mappedStorySteps: referencedSteps.size,
      productionNodes: dataset.nodes.length,
      productionEdges: dataset.edges.length,
    },
  })
}

export function buildGreekCoreEightAgesRoute(dataset: MythDataset): TimelineRoute {
  return buildTimelineRoute(dataset, greekCoreEightAgesProgramme, excludedReason)
}

export function buildNorseFenrirBindingRoute(dataset: MythDataset): TimelineRoute {
  return buildTimelineRoute(dataset, norseFenrirBindingProgramme, norseExcludedReason)
}

export function buildTimelineRouteForDataset(dataset: MythDataset): TimelineRoute | undefined {
  const programme = timelineProgrammeForDataset(dataset.manifest.dataset)
  if (!programme) return undefined
  return buildTimelineRoute(
    dataset,
    programme,
    dataset.manifest.dataset === 'norse-mythology-fenrir' ? norseExcludedReason : excludedReason,
  )
}

export interface TimelinePosition {
  eraId: string
  subEraId: string
  tick: number
}

export interface TimelineFlatTick {
  eraId: string
  subEraId: string
  eraOrder: number
  subEraOrder: number
  tick: TimelineTick
}

export interface TimelineFlatScene {
  eraId: string
  subEraId: string
  eraOrder: number
  subEraOrder: number
  scene: TimelineScene
}

export function flattenTimeline(route: TimelineRoute): TimelineFlatTick[] {
  return route.eras.flatMap((era) => era.subEras.flatMap((subEra) => subEra.ticks.map((tick) => ({
    eraId: era.id,
    subEraId: subEra.id,
    eraOrder: era.order,
    subEraOrder: subEra.order,
    tick,
  }))))
}

export function flattenTimelineScenes(route: TimelineRoute): TimelineFlatScene[] {
  return route.eras.flatMap((era) => era.subEras.flatMap((subEra) => subEra.scenes.map((scene) => ({
    eraId: era.id,
    subEraId: subEra.id,
    eraOrder: era.order,
    subEraOrder: subEra.order,
    scene,
  }))))
}

export function findTimelineScene(route: TimelineRoute, sceneId?: string): TimelineFlatScene | undefined {
  const scenes = flattenTimelineScenes(route)
  return scenes.find((item) => item.scene.id === sceneId) ?? scenes[0]
}

export function timelinePositionForScene(route: TimelineRoute, sceneId?: string): TimelinePosition {
  const match = findTimelineScene(route, sceneId)
  if (!match) {
    const era = route.eras[0]
    return { eraId: era.id, subEraId: era.subEras[0].id, tick: 0 }
  }
  return { eraId: match.eraId, subEraId: match.subEraId, tick: match.scene.endTick }
}

export function timelineCameraTargetsForScene(
  scene: TimelineScene,
  subEra: TimelineSubEra,
  dataset: Pick<MythDataset, 'nodes' | 'edges' | 'stories'>,
): TimelineTarget[] {
  const nodeById = new Map(dataset.nodes.map((node) => [node.id, node]))
  const edgeById = new Map(dataset.edges.map((edge) => [edge.id, edge]))
  const sceneOperationIds = new Set(scene.operationIds)
  const sceneTicks = subEra.ticks.filter((tick) => sceneOperationIds.has(tick.id))
  const sceneGraph = timelineSceneGraphTargetsForScene(scene, subEra, dataset)
  const seen = new Set<string>()
  const targets: TimelineTarget[] = []
  const addTarget = (target: TimelineTarget) => {
    const key = targetKey(target)
    if (seen.has(key)) return
    seen.add(key)
    targets.push(target)
  }
  const addNodeTarget = (nodeId: string) => {
    if (nodeById.has(nodeId)) addTarget({ kind: 'node', nodeId })
  }
  const addEdgeEndpointTargets = (edgeId: string) => {
    const edge = edgeById.get(edgeId)
    if (!edge) return
    addNodeTarget(edge.source)
    addNodeTarget(edge.target)
  }
  const eventNodeIdsForTarget = (target: TimelineTarget) => {
    if (target.kind === 'node') {
      return nodeById.get(target.nodeId)?.type === 'event' ? [target.nodeId] : []
    }
    const edge = edgeById.get(target.edgeId)
    if (!edge) return []
    return [edge.source, edge.target].filter((nodeId) => nodeById.get(nodeId)?.type === 'event')
  }

  addTarget(scene.primaryFocus)
  scene.focusPath.forEach(addTarget)
  sceneGraph.nodeIds.forEach(addNodeTarget)
  sceneGraph.edgeIds.forEach(addEdgeEndpointTargets)

  const primaryEventNodeIds = new Set([
    ...eventNodeIdsForTarget(scene.primaryFocus),
    ...scene.focusPath.flatMap(eventNodeIdsForTarget),
  ])
  primaryEventNodeIds.forEach((eventNodeId) => {
    const edgeIds = sceneGraph.edgeIds.size ? sceneGraph.edgeIds : new Set(sceneTicks.flatMap((tick) => (
      tick.target.kind === 'edge' ? [tick.target.edgeId] : []
    )))
    edgeIds.forEach((edgeId) => {
      const edge = edgeById.get(edgeId)
      if (!edge || (edge.source !== eventNodeId && edge.target !== eventNodeId)) return
      addTarget({ kind: 'edge', edgeId })
    })
  })

  return targets
}

export function timelineSceneGraphTargetsForScene(
  scene: Pick<TimelineScene, 'sourceSteps' | 'operationIds' | 'graphNodeIds' | 'graphEdgeIds'>,
  subEra: Pick<TimelineSubEra, 'ticks'>,
  dataset: Pick<MythDataset, 'edges' | 'stories'> & { nodes?: ReadonlyArray<Pick<GraphNode, 'id'>> },
) {
  const nodeIds = new Set<string>(scene.graphNodeIds ?? [])
  const edgeIds = new Set<string>(scene.graphEdgeIds ?? [])
  if (nodeIds.size === 0 && edgeIds.size === 0) {
    scene.sourceSteps.forEach((sourceStep) => {
      const story = dataset.stories.find((item) => item.id === sourceStep.storyId)
      const step = story?.steps.find((item) => item.id === sourceStep.stepId)
      step?.nodeIds.forEach((id) => nodeIds.add(id))
      step?.edgeIds.forEach((id) => edgeIds.add(id))
    })
  }

  const operationIds = new Set(scene.operationIds)
  subEra.ticks.forEach((tick) => {
    if (!operationIds.has(tick.id)) return
    if (tick.target.kind === 'node') nodeIds.add(tick.target.nodeId)
    else edgeIds.add(tick.target.edgeId)
  })

  const productionNodeIds = dataset.nodes ? new Set(dataset.nodes.map((node) => node.id)) : undefined
  edgeIds.forEach((edgeId) => {
    const edge = dataset.edges.find((item) => item.id === edgeId)
    if (!edge) return
    nodeIds.add(edge.source)
    nodeIds.add(edge.target)
  })

  if (productionNodeIds) {
    for (const nodeId of [...nodeIds]) {
      if (!productionNodeIds.has(nodeId)) nodeIds.delete(nodeId)
    }
  }

  return { nodeIds, edgeIds }
}

export function timelineSceneForPosition(route: TimelineRoute, position: TimelinePosition): TimelineFlatScene | undefined {
  const resolved = resolveTimelinePosition(route, position)
  const scenes = flattenTimelineScenes(route)
  return scenes.find((item) => item.eraId === resolved.eraId
    && item.subEraId === resolved.subEraId
    && item.scene.endTick >= resolved.tick
    && item.scene.startTick < Math.max(resolved.tick, 1))
    ?? scenes.find((item) => item.eraId === resolved.eraId && item.subEraId === resolved.subEraId)
}

export function nextTimelineScene(
  route: TimelineRoute,
  sceneId: string | undefined,
  scope: 'era' | 'all' = 'all',
): TimelineFlatScene | undefined {
  const scenes = flattenTimelineScenes(route)
  const currentIndex = Math.max(0, scenes.findIndex((item) => item.scene.id === sceneId))
  const current = scenes[currentIndex]
  const next = scenes[currentIndex + 1]
  if (!next || (scope === 'era' && current && next.eraId !== current.eraId)) return undefined
  return next
}

export function previousTimelineScene(route: TimelineRoute, sceneId?: string): TimelineFlatScene | undefined {
  const scenes = flattenTimelineScenes(route)
  const currentIndex = scenes.findIndex((item) => item.scene.id === sceneId)
  return currentIndex > 0 ? scenes[currentIndex - 1] : undefined
}

export function firstTimelineScene(route: TimelineRoute, eraId?: string): TimelineFlatScene | undefined {
  return flattenTimelineScenes(route).find((item) => !eraId || item.eraId === eraId)
}

export function resolveTimelinePosition(route: TimelineRoute, requested: Partial<TimelinePosition>): TimelinePosition {
  const era = route.eras.find((item) => item.id === requested.eraId) ?? route.eras[0]
  const subEra = era.subEras.find((item) => item.id === requested.subEraId) ?? era.subEras[0]
  const tick = Math.min(Math.max(Number.isInteger(requested.tick) ? requested.tick! : 0, 0), subEra.ticks.length)
  return { eraId: era.id, subEraId: subEra.id, tick }
}

export function timelineSnapshot(route: TimelineRoute, position: TimelinePosition) {
  const resolved = resolveTimelinePosition(route, position)
  const revealedNodeIds = new Set<string>()
  const revealedEdgeIds = new Set<string>()
  const currentSubEraNodeIds = new Set<string>()
  const currentSubEraEdgeIds = new Set<string>()
  let activeTick: TimelineTick | undefined
  let currentStoryStep: { storyId: string; stepId: string } | undefined

  for (const era of route.eras) {
    for (const subEra of era.subEras) {
      const isBefore = era.order < route.eras.find((item) => item.id === resolved.eraId)!.order
        || (era.id === resolved.eraId && subEra.order < era.subEras.find((item) => item.id === resolved.subEraId)!.order)
      const isCurrent = era.id === resolved.eraId && subEra.id === resolved.subEraId
      if (!isBefore && !isCurrent) continue
      const ticks = isCurrent ? subEra.ticks.slice(0, resolved.tick) : subEra.ticks
      ticks.forEach((tick) => {
        if (tick.target.kind === 'node') {
          revealedNodeIds.add(tick.target.nodeId)
          if (isCurrent) currentSubEraNodeIds.add(tick.target.nodeId)
        }
        else {
          revealedEdgeIds.add(tick.target.edgeId)
          if (isCurrent) currentSubEraEdgeIds.add(tick.target.edgeId)
        }
      })
      if (isCurrent && resolved.tick > 0) {
        activeTick = subEra.ticks[resolved.tick - 1]
        if (activeTick?.storyId && activeTick.stepId) {
          currentStoryStep = { storyId: activeTick.storyId, stepId: activeTick.stepId }
        }
      }
    }
  }

  return {
    position: resolved,
    revealedNodeIds,
    revealedEdgeIds,
    currentSubEraNodeIds,
    currentSubEraEdgeIds,
    activeTick,
    currentStoryStep,
  }
}

export function findTimelineIntroduction(route: TimelineRoute, target: TimelineTarget): TimelinePosition | undefined {
  const match = flattenTimeline(route).find((item) => item.tick.mode === 'introduce' && targetKey(item.tick.target) === targetKey(target))
  if (!match) return undefined
  return { eraId: match.eraId, subEraId: match.subEraId, tick: match.tick.order }
}

export function timelineScopeStart(route: TimelineRoute, position: TimelinePosition, scope: 'sub-era' | 'era' | 'all'): TimelinePosition {
  const resolved = resolveTimelinePosition(route, position)
  if (scope === 'sub-era') return { ...resolved, tick: 0 }
  if (scope === 'era') {
    const era = route.eras.find((item) => item.id === resolved.eraId)!
    return { eraId: era.id, subEraId: era.subEras[0].id, tick: 0 }
  }
  return { eraId: route.eras[0].id, subEraId: route.eras[0].subEras[0].id, tick: 0 }
}

export function nextTimelinePosition(
  route: TimelineRoute,
  position: TimelinePosition,
  scope: 'sub-era' | 'era' | 'all',
): TimelinePosition | undefined {
  const resolved = resolveTimelinePosition(route, position)
  const eraIndex = route.eras.findIndex((item) => item.id === resolved.eraId)
  const era = route.eras[eraIndex]
  const subEraIndex = era.subEras.findIndex((item) => item.id === resolved.subEraId)
  const subEra = era.subEras[subEraIndex]
  if (resolved.tick < subEra.ticks.length) return { ...resolved, tick: resolved.tick + 1 }
  if (scope === 'sub-era') return undefined
  if (subEraIndex < era.subEras.length - 1) {
    const nextSubEra = era.subEras[subEraIndex + 1]
    return nextSubEra.ticks.length
      ? { eraId: era.id, subEraId: nextSubEra.id, tick: 1 }
      : nextTimelinePosition(route, { eraId: era.id, subEraId: nextSubEra.id, tick: 0 }, scope)
  }
  if (scope === 'era' || eraIndex >= route.eras.length - 1) return undefined
  const nextEra = route.eras[eraIndex + 1]
  const nextSubEra = nextEra.subEras[0]
  return nextSubEra.ticks.length
    ? { eraId: nextEra.id, subEraId: nextSubEra.id, tick: 1 }
    : nextTimelinePosition(route, { eraId: nextEra.id, subEraId: nextSubEra.id, tick: 0 }, scope)
}

export function timelineEdgesForSnapshot(
  dataset: Pick<MythDataset, 'edges'>,
  route: TimelineRoute,
  position: TimelinePosition,
): GraphEdge[] {
  const snapshot = timelineSnapshot(route, position)
  return dataset.edges.filter((edge) => snapshot.currentSubEraEdgeIds.has(edge.id))
}
